import { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { BarChart3, Table2 } from 'lucide-react';
import { ScrollArea } from './ScrollArea';

const COLORS = ['#5b63ea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function detectChartType(columns, rows) {
  if (!columns?.length || !rows?.length) return null;
  if (columns.length === 1 && rows.length === 1) return 'big_number';

  const firstRow = rows[0];
  const types = columns.map((col, i) => {
    const val = firstRow[i];
    if (typeof val === 'number') return 'number';
    if (typeof val === 'string' && /^\d{4}-\d{2}/.test(val)) return 'date';
    return 'text';
  });

  const numCols = types.filter(t => t === 'number');
  const textCols = types.filter(t => t === 'text');
  const dateCols = types.filter(t => t === 'date');

  if (dateCols.length >= 1 && numCols.length >= 1 && rows.length >= 2) return 'line';
  if (textCols.length >= 1 && numCols.length >= 1 && rows.length <= 8 && rows.length >= 2) return 'donut';
  if (textCols.length >= 1 && numCols.length >= 1 && rows.length >= 2) return 'bar';
  return null;
}

function buildChartData(columns, rows, chartType) {
  return rows.map(row => {
    const obj = {};
    columns.forEach((col, i) => { obj[col] = row[i]; });
    return obj;
  });
}

function BigNumberDisplay({ columns, rows }) {
  const val = rows[0][0];
  const label = columns[0];
  return (
    <div data-testid="chart-big-number" className="flex flex-col items-center py-6">
      <span className="text-4xl font-bold text-[#5b63ea]" style={{ fontFamily: 'Outfit' }}>
        {typeof val === 'number' ? val.toLocaleString() : val}
      </span>
      <span className="text-sm text-[#6b7280] mt-1 capitalize">{label.replace(/_/g, ' ')}</span>
    </div>
  );
}

function AutoBarChart({ data, columns }) {
  const textCol = columns.find((_, i) => typeof data[0]?.[columns[i]] === 'string') || columns[0];
  const numCols = columns.filter((_, i) => typeof data[0]?.[columns[i]] === 'number');
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e6e8f2" />
        <XAxis dataKey={textCol} tick={{ fontSize: 11, fill: '#6b7280' }} />
        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e6e8f2', fontSize: 12 }} />
        {numCols.map((col, i) => (
          <Bar key={col} dataKey={col} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function AutoLineChart({ data, columns }) {
  const dateCol = columns.find((_, i) => typeof data[0]?.[columns[i]] === 'string' && /^\d{4}-\d{2}/.test(data[0]?.[columns[i]])) || columns[0];
  const numCols = columns.filter(c => c !== dateCol && typeof data[0]?.[c] === 'number');
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e6e8f2" />
        <XAxis dataKey={dateCol} tick={{ fontSize: 11, fill: '#6b7280' }} />
        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e6e8f2', fontSize: 12 }} />
        {numCols.map((col, i) => (
          <Line key={col} type="monotone" dataKey={col} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function AutoDonutChart({ data, columns }) {
  const textCol = columns.find((_, i) => typeof data[0]?.[columns[i]] === 'string') || columns[0];
  const numCol = columns.find((_, i) => typeof data[0]?.[columns[i]] === 'number') || columns[1];
  const pieData = data.map(d => ({ name: d[textCol], value: d[numCol] }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: '#9ca3af' }}>
          {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e6e8f2', fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function AutoChart({ results }) {
  const [viewMode, setViewMode] = useState('chart');

  if (!results?.columns?.length || !results?.rows?.length) return null;

  const chartType = detectChartType(results.columns, results.rows);
  if (!chartType) return null;
  if (chartType === 'big_number') return <BigNumberDisplay columns={results.columns} rows={results.rows} />;

  const data = buildChartData(results.columns, results.rows, chartType);

  return (
    <div data-testid="auto-chart" className="border border-[#e6e8f2] rounded-lg overflow-hidden bg-white">
      <div className="flex items-center justify-between px-3 py-2 bg-[#f8f9fc] border-b border-[#e6e8f2]">
        <span className="text-xs text-[#6b7280] font-medium">{results.row_count} result{results.row_count !== 1 ? 's' : ''}</span>
        <div className="flex items-center gap-1 bg-white border border-[#e6e8f2] rounded-lg p-0.5">
          <button
            data-testid="view-chart-btn"
            onClick={() => setViewMode('chart')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all ${viewMode === 'chart' ? 'bg-[#5b63ea] text-white' : 'text-[#6b7280] hover:bg-[#f5f6fb]'}`}
          >
            <BarChart3 size={10} /> Chart
          </button>
          <button
            data-testid="view-table-btn"
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all ${viewMode === 'table' ? 'bg-[#5b63ea] text-white' : 'text-[#6b7280] hover:bg-[#f5f6fb]'}`}
          >
            <Table2 size={10} /> Table
          </button>
        </div>
      </div>
      <div className="p-3">
        {viewMode === 'chart' ? (
          <>
            {chartType === 'bar' && <AutoBarChart data={data} columns={results.columns} />}
            {chartType === 'line' && <AutoLineChart data={data} columns={results.columns} />}
            {chartType === 'donut' && <AutoDonutChart data={data} columns={results.columns} />}
          </>
        ) : (
          <ScrollArea className="overflow-x-auto overflow-y-auto max-h-[220px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#f8f9fc]">
                <tr>
                  {results.columns.map((col, i) => (
                    <th key={i} className="px-3 py-2 text-left font-semibold text-[#1e1e2e] border-b border-[#e6e8f2] whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.rows.map((row, ri) => (
                  <tr key={ri} className="hover:bg-[#f5f6fb] border-b border-[#e6e8f2] last:border-0">
                    {row.map((val, ci) => (
                      <td key={ci} className="px-3 py-1.5 text-[#4b5563] whitespace-nowrap">{val ?? '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
